<template>
  <Card v-bind="$attrs">
    <template #title>
      <div class="p-d-flex p-justify-between">
        <div>
          ความสัมพันธ์ของตำแหน่งและหลักสูตรการฝึกอบรม
        </div>
        <div>
          <Button
            icon="pi pi-plus"
            label="เพิ่มข้อมูลใหม่"
            @click="onNew"
          ></Button>
          <BackButton class="p-ml-2"></BackButton>
        </div>
      </div>
    </template>
    <template #content="">
      <DataTable
        :value="relations"
        paginator
        :rows="30"
        dataKey="id"
        class="p-datatable-sm"
        v-model:filters="relationFilter"
        :globalFilterFields="[
          'departmentCode',
          'departmentDescription',
          'divisionCode',
          'divisionDescription',
          'level',
          'courseId',
          'course.description',
        ]"
      >
        <template #header="">
          <div class="p-grid">
            <div class="p-col-12 p-lg-4 p-md-6">
              <div class="p-inputgroup">
                <span class="p-inputgroup-addon">
                  <i class="pi pi-search"></i>
                </span>
                <inputText
                  placeholder="ระบุคำค้น"
                  id="filter"
                  v-model="relationFilter.global.value"
                ></inputText>
              </div>
            </div>
          </div>
        </template>
        <template #empty="">ไม่พบข้อมูล</template>
        <template #footer=""
          >จำนวนทั้งหมด {{ relations.length }} รายการ</template
        >
        <Column header="ID" field="id" sortable></Column>
        <Column header="Department" field="departmentCode" sortable>
          <template #body="props">
            {{
              !props.data.departmentCode
                ? "-"
                : `${props.data.departmentCode} : ${props.data.departmentDescription}`
            }}
          </template>
        </Column>
        <Column header="Division" field="divisionCode" sortable>
          <template #body="props">
            {{
              !props.data.divisionCode
                ? "-"
                : `${props.data.divisionCode} : ${props.data.divisionDescription}`
            }}
          </template></Column
        >
        <Column header="ตำแหน่ง" field="position" sortable> </Column>
        <Column header="Level" field="level" sortable>
          <template #body="props">
            {{ !props.data.level ? "-" : props.data.level }}
          </template>
        </Column>
        <Column header="Course" field="courseId" sortable>
          <template #body="props">
            {{ props.data.courseId }} : {{ props.data.course.description }}
          </template>
        </Column>
        <Column headerStyle="width:8rem" bodyStyle="width:8rem">
          <template #body="props">
            <Button
              @click="onCopy(props.index)"
              class="p-button-rounded p-button-info"
              icon="pi pi-copy"
            ></Button>
            <Button
              @click="onDelete(props.index)"
              class="p-button-rounded p-button-danger p-ml-2"
              icon="pi pi-minus"
            ></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
  <Dialog header="เพิ่มข้อมูล" v-model:visible="showAddDialog" modal>
    <div class="p-grid p-mt-3">
      <div class="p-col-12" v-if="v$.$errors.length > 0">
        <Message
          v-for="message in v$.$errors"
          :key="message.$uid"
          severity="error"
          >{{ message.$message }}</Message
        >
      </div>
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <span class="p-float-label">
          <Dropdown
            id="department"
            :options="departments"
            v-model="relation.departmentCode"
            optionLabel="description"
            optionValue="code"
            showClear
            appendTo="body"
            filter
          >
            <template #option="props">
              {{ props.option.code }} - {{ props.option.description }}
            </template>
          </Dropdown>
          <label for="department">Department</label>
        </span>
      </div>
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <span class="p-float-label">
          <Dropdown
            id="division"
            :options="divisions"
            v-model="relation.divisionCode"
            optionLabel="description"
            optionValue="code"
            showClear
            appendTo="body"
            filter
          >
            <template #option="props">
              {{ props.option.code }} - {{ props.option.description }}
            </template>
          </Dropdown>
          <label for="division">Division</label>
        </span>
      </div>
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <span class="p-float-label">
          <InputNumber
            id="level"
            v-model="relation.level"
            :min="1"
            :max="8"
          ></InputNumber>
          <label for="level">Level</label>
        </span>
      </div>
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <span class="p-float-label">
          <Dropdown
            id="position"
            :options="positions"
            v-model="relation.position"
            optionLabel="description"
            optionValue="code"
            showClear
            appendTo="body"
            filter
          >
          </Dropdown>
          <label for="position">ตำแหน่ง</label>
        </span>
      </div>
      <div class="p-col-12 p-fluid p-field">
        <span class="p-float-label">
          <InputCourse
            :class="{ 'p-invalid': v$.relation.courseId.$error }"
            id="courseId"
            v-model="relation.courseId"
          ></InputCourse>
          <label for="courseId">ระบุ Course</label>
        </span>
      </div>
    </div>
    <template #footer="">
      <Button icon="pi pi-check" label="ยืนยัน" @click="onSubmitNew"></Button>
      <Button
        icon="pi pi-times"
        class="p-button-warning"
        label="ยกเลิก"
        @click="showAddDialog = false"
      ></Button>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import {
  CourseRelation,
  Course,
  DropdownDto,
  GetCourseRelationResponse,
  UpsertCourseRelationRequest,
} from "@/types";
import InputCourse from "@/components/InputCourse.vue";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { clone } from "ramda";
import { FilterMatchMode } from "primevue/api";

export default defineComponent({
  name: "CourseRelationList",
  components: { InputCourse },
  setup() {
    const initial: CourseRelation = {
      id: 0,
      divisionCode: null,
      departmentCode: null,
      position: null,
      level: null,
      courseId: null,
      course: null,
      divisionDescription: null,
      departmentDescription: null,
    };
    const { axios, error, success, setLoading, formatAppDate } = usePlugin();
    const relations = ref<CourseRelation[]>([]);
    const departments = ref<DropdownDto[]>([]);
    const divisions = ref<DropdownDto[]>([]);
    const positions = ref<DropdownDto[]>([]);
    const relation = ref<CourseRelation>(initial);
    const relationFilter = ref({
      global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    });
    const showAddDialog = ref(false);
    const showDivision = (divisionCode: string) => {
      return !divisionCode
        ? "-"
        : `${divisionCode} : ${divisions.value.find(
            (x) => x.code === divisionCode
          )?.description ?? "N/A"}`;
    };

    const showDepartment = (departmentCode: string) => {
      return !departmentCode
        ? "-"
        : `${departmentCode} : ${departments.value.find(
            (x) => x.code === departmentCode
          )?.description ?? "N/A"}`;
    };

    const checkDuplicate = () => {
      return (
        relations.value.findIndex(
          (e) =>
            e.divisionCode === relation.value.divisionCode &&
            e.departmentCode === relation.value.departmentCode &&
            e.level === relation.value.level &&
            e.courseId === relation.value.courseId
        ) < 0
      );
    };
    const rules = computed(() => ({
      relation: {
        duplicate: {
          duplicate: helpers.withMessage(
            "พบข้อมูลแล้ว กรุณาระบุข้อมูลชุดอื่น ๆ",
            checkDuplicate
          ),
        },
        courseId: {
          required: helpers.withMessage("กรุณาระบุ Course", required),
        },
      },
    }));
    const v$ = useVuelidate(rules, { relation }, { $autoDirty: true });

    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetCourseRelationResponse>(
          "api/v1/services/courseRelation",
          {}
        );
        relations.value = result.data.relations ?? [];
        divisions.value = result.data.divisions ?? [];
        positions.value = result.data.positions ?? [];
        departments.value = result.data.departments ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onNew = () => {
      relation.value = clone(initial);
      v$.value.$reset();
      showAddDialog.value = true;
      
    };

    const onSubmitNew = async () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      try {
        await axios.post("api/v1/services/courseRelation/upsert", {
          relation: [relation.value],
        } as UpsertCourseRelationRequest);
        success("ลบข้อมูลแล้ว");
        loadDocument();
        showAddDialog.value = false;
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onDelete = async (index: number) => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/courseRelation/upsert", {
          relation: [relations.value[index]],
          delete: true,
        } as UpsertCourseRelationRequest);
        success("ลบข้อมูลแล้ว");
        relations.value.splice(index, 1);
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onCopy = (index: number) => {
      relation.value = clone(relations.value[index]);
      relation.value.id = 0;
      v$.value.$reset();
      showAddDialog.value = true;
    };

    onMounted(() => loadDocument());

    return {
      relations,
      relationFilter,
      showAddDialog,
      relation,
      departments,
      divisions,
      positions,
      onNew,
      v$,
      onSubmitNew,
      onDelete,
      onCopy,
      showDivision,
      showDepartment,
    };
  },
});
</script>

<style></style>
